import { Controller } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import CDatePicker from "../../../../components/CDatePicker";
import CInput from "../../../../components/CInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApiData, postFormData } from "../../../../utils/APIHelper";
import { toast } from "react-toastify";
import MilestoneCard from "./MilestoneCard";
import UploadSection from "./UploadSection";
import IssuesSection from "./IssuesSection";
import IssueDetailModal from "./IssueDetailModal";
import AddIssueLogModal from "./AddIssueLogModal";

export default function ProjectUpdateForm({
  form,
  projectId,
  selectedRow,
  createdUpdateId,
  handleBackToList,
  uploadedFiles,
  setUploadedFiles,
  selectedMilestones,
  setSelectedMilestones,
  milestoneProgress = {},
  setMilestoneProgress,
  milestoneNotes = {},
  setMilestoneNotes,
  loading,
  isViewMode,
  setIsViewMode,
  hasUpdateId = true,
  activeTab: activeTabProp,
  setActiveTab: setActiveTabProp,
  isCreateMode,
  projectDetails,
}) {
  const {
    control,
    formState: { errors },
    reset,
  } = form;

  const effectiveUpdateId =
    selectedRow?.update_id != null
      ? String(selectedRow.update_id)
      : createdUpdateId;
  const isEdit = !!effectiveUpdateId;
  const [uploading, setUploading] = useState(false);
  const [activeTabLocal, setActiveTabLocal] = useState("summary");
  const [activitySubTab, setActivitySubTab] = useState(() =>
    isCreateMode ? "annotate" : "gallery"
  );
  const [singleUpdateData, setSingleUpdateData] = useState(null);
  const [singleUpdateLoading, setSingleUpdateLoading] = useState(false);
  const [updatesFilesData, setUpdatesFilesData] = useState(null);
  const [milestonesTabLoading, setMilestonesTabLoading] = useState(false);
  const previousActiveTabRef = useRef(activeTabProp ?? "summary");

  const activeTab = activeTabProp ?? activeTabLocal;
  const setActiveTab = setActiveTabProp ?? setActiveTabLocal;

  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [logIssueModalOpen, setLogIssueModalOpen] = useState(false);
  const issuesRefetchRef = useRef(null);

  const tabs = [
    { slug: "summary", title: "Summary" },
    { slug: "activities", title: "Activity Photos", disabled: !hasUpdateId },
    {
      slug: "milestones",
      title: "Impacted Milestones",
      disabled: !hasUpdateId,
    },
    { slug: "issues", title: "Issues", disabled: !hasUpdateId },
  ];

  const refDataMilestones = singleUpdateData?.ref_data ?? [];
  const activityOptions = useMemo(
    () =>
      (projectDetails?.milestone || []).map((m) => ({
        value: m.id,
        label: String(m.milestone_name || m.name || `Milestone ${m.id}`).trim(),
      })),
    [projectDetails?.milestone]
  );

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setSingleUpdateLoading(true);
    getApiData(`api/project/update/${effectiveUpdateId}`, "GET")
      .then((response) => {
        if (cancelled) return;
        if (response?.success && response?.data?.length > 0) {
          setSingleUpdateData(response.data[0]);
        }
      })
      .catch(() => {
        if (!cancelled) setSingleUpdateData(null);
      })
      .finally(() => {
        if (!cancelled) setSingleUpdateLoading(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, effectiveUpdateId]);

  useEffect(() => {
    if (!isEdit || !effectiveUpdateId) return;
    let cancelled = false;
    getApiData(
      `api/project/updates/files?ref_id=${effectiveUpdateId}&ref_type=PROJECT_UPDATE`,
      "GET"
    )
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []);
        setUpdatesFilesData(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setUpdatesFilesData(null);
      });
    return () => { cancelled = true; };
  }, [isEdit, effectiveUpdateId]);

  const refetchUpdatesFiles = useCallback(() => {
    if (!isEdit || !effectiveUpdateId) return;
    getApiData(
      `api/project/updates/files?ref_id=${effectiveUpdateId}&ref_type=PROJECT_UPDATE`,
      "GET"
    )
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []);
        setUpdatesFilesData(Array.isArray(list) ? list : []);
      })
      .catch(() => setUpdatesFilesData(null));
  }, [isEdit, effectiveUpdateId]);

  const refetchSingleUpdate = useCallback(() => {
    if (!isEdit || !effectiveUpdateId) return Promise.resolve(null);
    return getApiData(`api/project/update/${effectiveUpdateId}`, "GET").then(
      (response) => {
        if (response?.success && response?.data?.length > 0) {
          const data = response.data[0];
          setSingleUpdateData(data);
          return data;
        }
        return null;
      }
    );
  }, [isEdit, effectiveUpdateId]);

  useEffect(() => {
    const prevTab = previousActiveTabRef.current;
    previousActiveTabRef.current = activeTab;
    if (activeTab !== "milestones" || prevTab === "milestones") return;
    if (!isEdit) return;
    let cancelled = false;
    setMilestonesTabLoading(true);
    refetchSingleUpdate()
      .then((data) => {
        if (cancelled || !data) return;
        const list = Array.isArray(data.ref_data) ? data.ref_data : [];
        setSelectedMilestones(list);
        if (list.length > 0 && setMilestoneProgress) {
          setMilestoneProgress((prev) => {
            const next = { ...prev };
            list.forEach((m) => {
              if (m.id != null) next[m.id] = m.progress ?? 0;
            });
            return next;
          });
        }
        if (list.length > 0 && setMilestoneNotes) {
          setMilestoneNotes((prev) => {
            const next = { ...prev };
            list.forEach((m) => {
              if (m.id != null && (m.notes ?? "").trim()) next[m.id] = (m.notes ?? "").trim();
            });
            return next;
          });
        }
      })
      .finally(() => {
        if (!cancelled) setMilestonesTabLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, isEdit, refetchSingleUpdate, setSelectedMilestones, setMilestoneProgress, setMilestoneNotes]);

  useEffect(() => {
    if (isEdit && singleUpdateData) {
      reset({
        name: singleUpdateData.name || `${projectDetails?.name} Update`,
        date: singleUpdateData.update_date
          ? new Date(singleUpdateData.update_date)
          : new Date(),
        description: singleUpdateData.message || "",
      });
      setSelectedMilestones(singleUpdateData.ref_data || []);
      if (singleUpdateData.ref_data?.length && setMilestoneProgress) {
        setMilestoneProgress((prev) => {
          const next = { ...prev };
          singleUpdateData.ref_data.forEach((m) => {
            if (m.id != null) {
              next[m.id] = m.progress ?? m.milestone_percentage ?? 0;
            }
          });
          return next;
        });
      }
      if (singleUpdateData.ref_data?.length && setMilestoneNotes) {
        setMilestoneNotes((prev) => {
          const next = { ...prev };
          singleUpdateData.ref_data.forEach((m) => {
            if (m.id != null && (m.notes ?? "").trim()) {
              next[m.id] = (m.notes ?? "").trim();
            }
          });
          return next;
        });
      }
    } else if (!isEdit) {
      reset({
        name: `${projectDetails?.name} Update` || "",
        date: new Date(),
        description: "",
      });
      setSelectedMilestones([]);
      setUploadedFiles([]);
    }
  }, [
    singleUpdateData,
    isEdit,
    reset,
    projectDetails,
    setSelectedMilestones,
    setMilestoneProgress,
    setMilestoneNotes,
    setUploadedFiles,
  ]);

  const parseMeta = useCallback((raw) => {
    if (raw == null) return { room: null, milestones: [] };
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw) || { room: null, milestones: [] };
      } catch {
        return { room: null, milestones: [] };
      }
    }
    return typeof raw === "object" ? raw : { room: null, milestones: [] };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const mapItem = (item, index, prefix = "design") => {
      const isPdf =
        item.file_extention === "application/pdf" ||
        item.type === "application/pdf" ||
        item.extension === "application/pdf";
      const url = item.path || item.url || item.image_url;
      const ext = item.file_extention || item.extension || item.type;
      return {
        ...item,
        id: item.id,
        uniqueId: item.uniqueId || `${prefix}_${item.id}_${index}`,
        url,
        path: url,
        type: isPdf ? "application/pdf" : ext,
        file_extention: isPdf ? "application/pdf" : ext,
        meta_data: parseMeta(item.meta_data),
      };
    };
    if (updatesFilesData !== null && updatesFilesData !== undefined) {
      const fromServer = (updatesFilesData || []).map((item, index) =>
        mapItem(item, index, "design")
      );
      setUploadedFiles((prev) => {
        const pending = (prev || []).filter((f) => !f.id);
        return [...fromServer, ...pending];
      });
      return;
    }
    if (singleUpdateData == null) return;
    const images = (singleUpdateData.images || []).map((item, index) =>
      mapItem(item, index, "design")
    );
    const pdfs = (singleUpdateData.pdf_files || []).map((item, index) =>
      mapItem({ ...item, type: "application/pdf", extension: "application/pdf" }, index, "design_pdf")
    );
    setUploadedFiles((prev) => {
      const pending = (prev || []).filter((f) => !f.id);
      return [...images, ...pdfs, ...pending];
    });
  }, [isEdit, singleUpdateData, updatesFilesData, parseMeta]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    const MAX_SIZE_BYTES = 50 * 1024 * 1024;
    const maxFiles = 25;

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Only image files (JPEG, PNG, GIF, WebP) and PDFs are allowed."
        );
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds 50 MB limit`);
        continue;
      }
      if (uploadedFiles.length >= maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed.`);
        break;
      }
      const uniqueId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setUploadedFiles((prev) => [
        ...prev,
        {
          file,
          url: URL.createObjectURL(file),
          type: file.type,
          name: file.name,
          size: file.size,
          uniqueId,
          meta_data: { room: null, milestones: [] },
        },
      ]);
    }
  };

  const handleFileDelete = async (fileObj) => {
    const fileId = fileObj.id || fileObj.file_id;
    if (isEdit && fileId) {
      try {
        const response = await getApiData(
          `api/project/updates/files/${fileId}`,
          "DELETE"
        );
        if (response?.success !== false) {
          setUploadedFiles((prev) =>
            prev.filter((f) => (f.id || f.file_id) !== fileId)
          );
          refetchUpdatesFiles();
          const refetched = await refetchSingleUpdate();
          if (refetched?.ref_data != null) {
            setSelectedMilestones(refetched.ref_data);
          }
          toast.success("File deleted");
        } else {
          toast.error(response?.message || "Delete failed");
        }
      } catch (err) {
        toast.error(err?.message || "An error occurred while deleting the file");
      }
    } else {
      setUploadedFiles((prev) =>
        prev.filter(
          (f) => f.uniqueId !== fileObj.uniqueId && f !== fileObj
        )
      );
    }
  };

  const handleFileMetaDataChange = useCallback((fileUniqueId, meta_data) => {
    setUploadedFiles((prev) =>
      prev.map((f) =>
        f.uniqueId === fileUniqueId ? { ...f, meta_data } : f
      )
    );
  }, []);

  const handleSaveAnnotatedImage = useCallback(
    async (fileObj, annotationsMeta) => {
      if (fileObj.id) return;
      if (!fileObj.file) {
        toast.error("No image to upload");
        return;
      }
      const meta_data =
        annotationsMeta && typeof annotationsMeta === "object"
          ? annotationsMeta
          : { room: null, milestones: [], tags: [] };
      const fd = new FormData();
      fd.append("ref_id", effectiveUpdateId);
      fd.append("ref_type", "PROJECT_UPDATE");
      fd.append("type", "");
      fd.append("file_1", fileObj.file, fileObj.file.name || "image.png");
      fd.append("meta_data", JSON.stringify(meta_data));
      fd.append("tag", "");
      try {
        const res = await postFormData("api/project/updates/files", fd);
        if (res?.success) {
          toast.success("Saved");
          setUploadedFiles((prev) =>
            prev.filter((f) => f.uniqueId !== fileObj.uniqueId)
          );
          refetchUpdatesFiles();
          const refetched = await refetchSingleUpdate();
          const list = Array.isArray(refetched?.ref_data) ? refetched.ref_data : [];
          setSelectedMilestones(list);
          if (list.length > 0 && setMilestoneProgress) {
            setMilestoneProgress((prev) => {
              const next = { ...prev };
              list.forEach((m) => {
                if (m.id != null) next[m.id] = m.progress ?? 0;
              });
              return next;
            });
          }
          if (list.length > 0 && setMilestoneNotes) {
            setMilestoneNotes((prev) => {
              const next = { ...prev };
              list.forEach((m) => {
                if (m.id != null && (m.notes ?? "").trim()) next[m.id] = (m.notes ?? "").trim();
              });
              return next;
            });
          }
        } else {
          toast.error(res?.message || "Save failed");
        }
      } catch (err) {
        toast.error(err?.message || "Failed to save image");
      }
    },
    [effectiveUpdateId, parseMeta, refetchUpdatesFiles, refetchSingleUpdate, setMilestoneProgress, setMilestoneNotes]
  );

  useEffect(() => {
    setActivitySubTab(isCreateMode ? "annotate" : "gallery");
  }, [isCreateMode]);

  const handleTabChange = (tab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
  };


  return (
    <>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          paddingBottom: "8px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", overflow: "auto", alignItems: "center" }}>
          {tabs.map((tab) => (
            <button
              key={tab.slug}
              type="button"
              onClick={() => !tab.disabled && handleTabChange(tab.slug)}
              disabled={tab.disabled}
              style={{
                minHeight: "36px",
                height: "36px",
                borderRadius: "12px",
                padding: "0 12px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                border:
                  activeTab === tab.slug
                    ? "1px solid #0d0d0d"
                    : "1px solid #d7d8d9",
                background:
                  activeTab === tab.slug ? "#0d0d0d" : "#fff",
                color: activeTab === tab.slug ? "#fff" : "#949DAA",
                cursor: tab.disabled ? "not-allowed" : "pointer",
                opacity: tab.disabled ? 0.5 : 1,
              }}
            >
              {tab.title}
            </button>
          ))}
        </div>

        {activeTab === "activities" && (
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => setActivitySubTab("annotate")}
              style={{
                minHeight: "36px",
                height: "36px",
                borderRadius: "8px",
                padding: "0 12px",
                fontSize: "14px",
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                background: activitySubTab === "annotate" ? "#0d0d0d" : "#f3f4f6",
                color: activitySubTab === "annotate" ? "#fff" : "#374151",
                whiteSpace: "nowrap",
              }}
            >
              Add Activity Photos
            </button>
            <button
              type="button"
              onClick={() => setActivitySubTab("gallery")}
              style={{
                minHeight: "36px",
                height: "36px",
                borderRadius: "8px",
                padding: "0 12px",
                fontSize: "14px",
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                background: activitySubTab === "gallery" ? "#0d0d0d" : "#f3f4f6",
                color: activitySubTab === "gallery" ? "#fff" : "#374151",
                whiteSpace: "nowrap",
              }}
            >
              Gallery
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 8px",
          }}
        >
          {activeTab === "summary" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                paddingTop: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Update title is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <CInput
                        label="Update Title"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        placeholder="Enter Update Title"
                        disabled={isViewMode}
                        error={error != null}
                        helpertext={error?.message || ""}
                      />
                    )}
                  />
                </div>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <CDatePicker
                          label="Update Date"
                          value={field.value ? new Date(field.value) : null}
                          onChange={(date) => field.onChange(date)}
                          disabled={isViewMode}
                          isProjectUpdate={true}
                          error={errors?.date != null}
                          helperText={errors?.date?.message}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              <div style={{ width: "100%" }}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: "Description is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <CInput
                      label="Progress Description"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      placeholder="Write your progress so far"
                      disabled={isViewMode}
                      error={error != null}
                      helpertext={error?.message || ""}
                      multiline
                      minRows={4}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {activeTab === "activities" && (
            <>
              <UploadSection
                uploadedFiles={uploadedFiles}
                uploading={uploading}
                handleFileChange={handleFileChange}
                handleDelete={handleFileDelete}
                isEdit={isEdit}
                disabled={isViewMode}
                effectiveUpdateId={effectiveUpdateId}
                updateId={effectiveUpdateId}
                isViewMode={isViewMode}
                activitySubTab={activitySubTab}
                setActivitySubTab={setActivitySubTab}
                projectDetails={projectDetails}
                otherImages={singleUpdateData?.images || []}
                otherPdfFiles={singleUpdateData?.pdf_files || []}
                onFileMetaDataChange={handleFileMetaDataChange}
                onSaveAnnotatedImage={handleSaveAnnotatedImage}
              />
            </>
          )}

          {activeTab === "milestones" && (
            <>
              {milestonesTabLoading || singleUpdateLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 48 }}>
                  <CircularProgress size={32} />
                </div>
              ) : (
                <MilestoneCard
                  projectDetails={projectDetails}
                  refDataMilestones={refDataMilestones}
                  selectedMilestones={selectedMilestones}
                  setSelectedMilestones={setSelectedMilestones}
                  milestoneProgress={milestoneProgress}
                  onProgressChange={(id, value) =>
                    setMilestoneProgress?.((prev) => ({ ...prev, [id]: value }))
                  }
                  milestoneNotes={milestoneNotes}
                  onNotesChange={(id, value) =>
                    setMilestoneNotes?.((prev) => ({ ...prev, [id]: value }))
                  }
                  disabled={isViewMode}
                />
              )}
            </>
          )}

          {activeTab === "issues" && (
            <IssuesSection
              projectId={projectId}
              onLogIssueClick={() => setLogIssueModalOpen(true)}
              onIssueClick={(id) => setSelectedIssueId(id)}
              refetchRef={issuesRefetchRef}
            />
          )}
        </div>
      </div>

      <IssueDetailModal
        open={!!selectedIssueId}
        issueId={selectedIssueId}
        onClose={() => setSelectedIssueId(null)}
        onSuccess={() => {
          issuesRefetchRef.current?.();
          setSelectedIssueId(null);
        }}
      />

      <AddIssueLogModal
        isOpen={logIssueModalOpen}
        onClose={() => setLogIssueModalOpen(false)}
        onSuccess={() => {
          issuesRefetchRef.current?.();
          setLogIssueModalOpen(false);
        }}
        projectId={projectId}
        activityOptions={activityOptions}
        sourceId={effectiveUpdateId}
      />
    </>
  );
}
